import { config } from '@/config'
import type {
  GeneratedImage,
  GeneratedTraits,
  GeneratorInput,
} from '@/modules/creature-generator/interface'
import { z } from 'zod'

const ApiErrorSchema = z
  .object({
    error: z.string(),
    code: z.string().optional(),
  })
  .passthrough()

const PlantIdentificationResponseSchema = z
  .object({
    species: z.string().optional(),
    commonName: z.string().optional(),
    confidence: z.number().optional(),
    biome: z.string().optional(),
    careNotes: z.array(z.string()).optional(),
  })
  .passthrough()

const EnvironmentInferenceResponseSchema = z
  .object({
    biome: z.string().optional(),
  })
  .passthrough()

const GeneratedTraitsSchema = z.object({
  name: z.string(),
  lore: z.string(),
  description: z.string(),
  traits: z.array(z.string()),
  signatureMove: z.string(),
})

const GeneratedImageSchema = z.object({
  imageUrl: z.string(),
})

const StartCreatureModelResponseSchema = z.object({
  taskId: z.string(),
})

const CreatureModelCompleteResponseSchema = z.object({
  modelUrl: z.string(),
  thumbnailUrl: z.string(),
  taskId: z.string(),
})

const CreatureModelProcessingResponseSchema = z.object({
  status: z.enum(['pending', 'processing']),
  progress: z.number().optional(),
})

const CreatureModelStatusResponseSchema = z.union([
  CreatureModelCompleteResponseSchema,
  CreatureModelProcessingResponseSchema,
])

export type PlantIdentificationApiResponse = z.infer<typeof PlantIdentificationResponseSchema>
export type EnvironmentInferenceApiResponse = z.infer<typeof EnvironmentInferenceResponseSchema>
export type CreatureModelStatusResponse = z.infer<typeof CreatureModelStatusResponseSchema>

export class ApiResponseError extends Error {
  readonly status: number
  readonly code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiResponseError'
    this.status = status
    this.code = code
  }
}

export function buildApiUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const normalizedBase = config.apiBaseUrl.replace(/\/$/, '')
  return `${normalizedBase}${normalizedPath}`
}

async function parseErrorResponse(response: Response): Promise<ApiResponseError> {
  try {
    const parsed = ApiErrorSchema.safeParse(await response.clone().json())
    if (parsed.success) {
      return new ApiResponseError(parsed.data.error, response.status, parsed.data.code)
    }
  } catch {
    // Fall through to stable generic response below.
  }

  return new ApiResponseError(`Request failed with status ${response.status}`, response.status)
}

async function parseJsonResponse<TSchema extends z.ZodTypeAny>(
  response: Response,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  if (!response.ok) {
    throw await parseErrorResponse(response)
  }

  let json: unknown
  try {
    json = await response.json()
  } catch {
    throw new ApiResponseError('Invalid JSON response', response.status, 'invalid_json')
  }

  const parsed = schema.safeParse(json)
  if (!parsed.success) {
    throw new ApiResponseError('Invalid API response', response.status, 'invalid_response')
  }

  return parsed.data
}

async function postJson<TSchema extends z.ZodTypeAny>(
  path: string,
  body: unknown,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  const response = await fetch(buildApiUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return parseJsonResponse(response, schema)
}

export function requestPlantIdentification(request: {
  imageBase64: string
  latitude?: number
  longitude?: number
}): Promise<PlantIdentificationApiResponse> {
  return postJson('/api/plants/identify', request, PlantIdentificationResponseSchema)
}

export function requestBiomeInference(
  latitude: number,
  longitude: number,
): Promise<EnvironmentInferenceApiResponse> {
  return postJson(
    '/api/environment/infer',
    { latitude, longitude },
    EnvironmentInferenceResponseSchema,
  )
}

export function requestCreatureTraits(input: GeneratorInput): Promise<GeneratedTraits> {
  return postJson('/api/creatures/traits', input, GeneratedTraitsSchema)
}

export function requestCreatureImage(
  traits: GeneratedTraits,
  prompt: string,
): Promise<GeneratedImage> {
  return postJson('/api/creatures/image', { traits, prompt }, GeneratedImageSchema)
}

export async function startCreatureModel(request: {
  imageUrl: string
  creatureName: string
}): Promise<string> {
  const result = await postJson('/api/creatures/model', request, StartCreatureModelResponseSchema)
  return result.taskId
}

export async function pollCreatureModel(taskId: string): Promise<CreatureModelStatusResponse> {
  const response = await fetch(
    buildApiUrl(`/api/creatures/model?taskId=${encodeURIComponent(taskId)}`),
  )

  if (response.status === 202) {
    try {
      return await parseJsonResponse(response, CreatureModelStatusResponseSchema)
    } catch {
      return { status: 'processing' }
    }
  }

  return parseJsonResponse(response, CreatureModelStatusResponseSchema)
}

export function buildPimlicoProxyUrl(): string {
  return buildApiUrl('/api/auth/pimlico')
}
